import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IniciativaFormatada } from 'types/iniciativa';
import { IniciativaHero } from 'pages/InscricaoIniciativaElbb/components/IniciativaHero';
import { IniciativaDetalhes } from 'pages/InscricaoIniciativaElbb/components/IniciativaDetalhes';
import { IniciativaDirecionador } from 'pages/InscricaoIniciativaElbb/components/IniciativaDirecionador';
import { IniciativaParticipantes } from 'pages/InscricaoIniciativaElbb/components/IniciativaParticipantes';
import { IniciativaActionBar } from 'pages/InscricaoIniciativaElbb/components/IniciativaActionBar';
import { useAuth } from 'context/AuthContext';
import { useModal } from 'context/ModalContext/Modal.context';
import { useRule } from 'hooks/useRule';
import ModalWarning from 'components/ui/ModalWarning';
import { clearRegistrationUser } from 'utils/fns';
import { confirmarParticipacaoElbb } from 'services/iniciativa-elbb.service';
import { useIsDateActive } from 'hooks/useControlDate';
import { PHASE_IDS } from 'utils/constants';
import { ModalEditarInscricao } from 'components/ui/ModalEditarInscricao';
import { IoPencil } from 'react-icons/io5';
import { FaTrophy } from 'react-icons/fa';

type IniciativaCardListagemProps = IniciativaFormatada & {
  onConfirm?: () => void;
};

export const IniciativaCardListagem: React.FC<IniciativaCardListagemProps> = ({ onConfirm, ...props }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const rule = useRule();
  const { showModal, closeModal } = useModal();
  const [isSending, setIsSending] = useState(false);
  const [ruleUrl, setRuleUrl] = useState('');

  useEffect(() => {
    if (rule) setRuleUrl(rule.url);
  }, [rule]);

  const currentPlayer = clearRegistrationUser(user?._id || '');
  const isInscrita = props.status === 'inscrita';
  const confirmedPlayers: string[] = (props.iniciativaOriginal as any)?.confirmedPlayers || [];
  const isConfirmed = confirmedPlayers.some((cp) => clearRegistrationUser(cp) === currentPlayer);
  const isRegistrationPeriodActive = useIsDateActive(PHASE_IDS.ELBB_REGISTRATION_PERIOD);

  const handleEditarInscricao = () => {
    showModal(
      'editar-inscricao',
      <ModalEditarInscricao
        iniciativaOriginal={props.iniciativaOriginal}
        onClose={closeModal}
        onSave={() => {
          closeModal();
          onConfirm?.();
        }}
      />,
      'editar-inscricao'
    );
  };

  const handleConfirmarParticipacao = () => {
    showModal(
      'confirm-participation',
      <ModalWarning
        rule={ruleUrl}
        title="Confirmar participação"
        subTitle="Confirme abaixo algumas informações importantes antes de finalizar."
        needInfo={true}
        needRule={true}
         description={
          <div className="flex flex-col gap-3 text-base">
            <p>Declaro que li e concordo com os termos estabelecidos no Regulamento do Prêmio ELBB.</p>
            <p>
              Estou ciente sobre os prazos de inscrição, requisitos de participação, critérios de avaliação e os meus direitos e deveres como participante do Prêmio ELBB.
            </p>
            <p>Comprometo-me a fornecer informações precisas e completas, além de seguir todas as regras e regulamentos da premiação.</p>
            <p>Autorizo a organização a usar minha imagem em fotos ou vídeos, bem como meus dados pessoais, para fins de divulgação.</p>
            <p>
              Tenho conhecimento de que será realizada a verificação dos requisitos de impedimentos, com base na situação apurada no primeiro dia útil após o encerramento das inscrições.
            </p>
          </div>
        }
        infoLabel="Li e estou de acordo com as informações disponibilizadas acima."
        ruleLabel={
          <>
            Li e estou de acordo com o{' '}
            <Link to="/regulamento-elbb" target="_blank" className="underline text-blue-bb-1 hover:no-underline">
              regulamento do Prêmio ELBB
            </Link>
            .
          </>
        }
        acceptTextClassName="text-base"
        btnText="Confirmar participação"
        hasBackButton={true}
        onBackButton={closeModal}
        showRuleLink={false}
        onClose={async () => {
          closeModal();
          setIsSending(true);
          try {
            await confirmarParticipacaoElbb(props.id, user!._id);
            onConfirm?.();
            navigate('/confirmacao-elbb');
          } catch (error) {
            console.error('Erro ao confirmar participação:', error);
            navigate('/confirmacao-elbb');
          } finally {
            setIsSending(false);
          }
        }}
      />,
      'confirm-participation'
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-md p-7 sm:p-4">
      {(props.classificadaEtapa1 || props.classificadaEtapa2) && (
        <div
          className="flex items-center gap-2 -mx-7 -mt-7 sm:-mx-4 sm:-mt-4 mb-5 px-5 py-2.5 rounded-t-2xl"
          style={{ background: props.classificadaEtapa2 ? '#9A7ED9' : '#3333BD' }}
        >
          <FaTrophy size={16} style={{ color: '#F6F630', flexShrink: 0 }} aria-hidden="true" />
          <span style={{ color: '#F6F630', fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            {props.classificadaEtapa2 ? 'CLASSIFICADA ETAPA 2' : 'CLASSIFICADA ETAPA 1'}
          </span>
        </div>
      )}
      <div className={`grid grid-cols-1 ${(props.documentacao || props.videoUrl) ? 'md:grid-cols-[340px_1fr]' : ''} gap-8 items-start`}>
        {/* Coluna esquerda — mídia */}
        {(props.documentacao || props.videoUrl) && (
          <div className="flex flex-col gap-5">
            <IniciativaHero
              imagem={props.imagem}
              titulo={props.titulo}
              categoria={props.categoria}
              corCategoria={props.corCategoria}
              documentacao={props.documentacao}
              videoUrl={props.videoUrl}
              originalVideoUrl={props.originalVideoUrl}
              totalElogios={props.totalElogios}
              totalCompartilhamentos={props.totalCompartilhamentos}
              totalFavoritos={props.totalFavoritos}
              elogiosPorTipo={props.elogiosPorTipo}
            >
              {isInscrita && (
                <button
                  onClick={handleEditarInscricao}
                  className="bg-transparent border-0 p-0 text-blue-bb-1 text-sm font-semibold cursor-pointer underline hover:no-underline self-start flex items-center gap-1"
                >
                  Editar
                  <IoPencil size={14} aria-hidden="true" />
                </button>
              )}
            </IniciativaHero>
          </div>
        )}

        {/* Coluna direita — detalhes */}
        <div className="flex flex-col gap-5">
          <IniciativaDetalhes
            titulo={props.titulo}
            tema={props.tema}
            descricao={props.descricao}
            categoria={props.categoria}
            categoryPremio={props.categoryPremio}
            corCategoria={props.corCategoria}
            cadastradoPor={props.cadastradoPor}
            cadastradoMatricula={props.cadastradoMatricula}
            cadastradoData={props.cadastradoData}
            inscritoPor={props.inscritoPor}
            inscritoMatricula={props.inscritoMatricula}
            inscritoData={props.inscritoData}
          />

          <IniciativaDirecionador
            direcionadorEstrategico={props.categoria}
            texto={props.iniciativaOriginal?.result || props.texto}
          />

          <IniciativaParticipantes participantes={props.participantes} />

          <IniciativaActionBar
            id={props.id}
            titulo={props.titulo}
            descricao={props.descricao}
            totalElogios={props.totalElogios}
            totalCompartilhamentos={props.totalCompartilhamentos}
            totalFavoritos={props.totalFavoritos}
            usuarioElogio={props.usuarioElogio}
            usuarioFavoritou={props.usuarioFavoritou}
            usuarioCompartilhou={props.usuarioCompartilhou}
          />
        </div>
      </div>

      <div className="pt-5 flex flex-col gap-3">
        {isInscrita && isConfirmed ? (
          <button
            disabled
            className="w-full bg-gray-100 text-gray-500 border border-gray-300 rounded-lg px-11 py-3.5 text-[15px] font-extrabold cursor-not-allowed tracking-wide"
          >
            Participação confirmada
          </button>
        ) : isInscrita && !isConfirmed ? (
          <button
            disabled={isSending}
            onClick={handleConfirmarParticipacao}
            className="w-full bg-yellow-bb-1 text-blue-bb-1 border-0 rounded-lg px-11 py-3.5 text-[15px] font-extrabold cursor-pointer tracking-wide hover:bg-yellow-bb-5 hover:-translate-y-px transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            CONFIRMAR INSCRIÇÃO
          </button>
        ) : isRegistrationPeriodActive ? (
          <button
            className="w-full bg-yellow-bb-1 text-blue-bb-1 border-0 rounded-lg px-11 py-3.5 text-[15px] font-extrabold cursor-pointer tracking-wide hover:bg-yellow-bb-5 hover:-translate-y-px transition-all"
            onClick={() => navigate(`/inscricao-iniciativa-elbb/${props.id}`, { state: { iniciativa: props } })}
          >
            INSCREVER INICIATIVA
          </button>
        ) : (
          <button
            disabled
            className="w-full bg-gray-100 text-gray-500 border border-gray-300 rounded-lg px-11 py-3.5 text-[15px] font-extrabold cursor-not-allowed tracking-wide"
          >
            INSCRIÇÕES ENCERRADAS
          </button>
        )}
      </div>
    </div>
  );
};
